import {
  AsyncFacetedFilter,
  AsyncFilterOption,
} from '@app/components/data-table/async-faceted-filter';
import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { useGetPurchasedGoodsItemsList } from '@shared/api/hooks/purchased-goods/items';
import { useDebounce } from '@shared/components/data-table/hooks/use-debounce';
import { useMemo, useState } from 'react';
import {
  useInsightsFilters,
  useInsightsItemSearchScope,
  useInsightsTableState,
} from '../hooks/use-insights-filters';

/**
 * The endpoint caps results rather than paginating, so searches only run from
 * this many characters — enough to keep result sets naturally under the cap.
 */
const MIN_SEARCH_LENGTH = 3;

/**
 * The Item filter pill — purchased goods are too numerous to preload, so the
 * option list is a backend search that runs from three typed characters; with
 * no search term the list shows the current selection, since the pill compacts
 * to "n selected" and this is where items are deselected. Searches carry the
 * active page scope, so only items within the current view are offered.
 * Selected uuids live in the URL like every other filter; their labels are
 * resolved through the same endpoint (needed on deep links, where the
 * selection was never in this session's search results).
 */
export function InsightsItemFilter() {
  const [filters, setFilters] = useInsightsFilters();
  const [, setTableState] = useInsightsTableState();
  const [searchTerm, setSearchTerm] = useState('');
  const { track } = useAnalytics();
  const debouncedSearch = useDebounce(searchTerm.trim(), 300);
  const hasSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH;
  const searchScope = useInsightsItemSearchScope();

  const { data: results, isFetching } = useGetPurchasedGoodsItemsList(
    { search: debouncedSearch, ...searchScope },
    { enabled: hasSearch }
  );

  // Resolve labels for the current selection (cheap and cached; also covers
  // selections restored from a shared URL).
  const { data: selectedItems } = useGetPurchasedGoodsItemsList(
    { item: filters.item },
    { enabled: filters.item.length > 0 }
  );

  const selected: AsyncFilterOption[] = useMemo(
    () =>
      filters.item.map((uuid) => {
        const match = (selectedItems ?? []).find((item) => item.uuid === uuid);
        return {
          value: uuid,
          label: match?.name ?? '…',
          sublabel: match?.externalId ?? undefined,
        };
      }),
    [filters.item, selectedItems]
  );

  // No search yet → the selection itself is the list, so it can be reviewed
  // and pruned. Once a searchable term is in, the server results take over.
  const options: AsyncFilterOption[] = useMemo(() => {
    if (!hasSearch) return searchTerm.trim() ? [] : selected;
    return (results ?? []).map((item) => ({
      value: item.uuid,
      label: item.name,
      sublabel: item.externalId ?? undefined,
    }));
  }, [hasSearch, searchTerm, results, selected]);

  // Cover the debounce window too, so keystrokes don't flash "no results".
  const isSearching =
    searchTerm.trim().length >= MIN_SEARCH_LENGTH &&
    (isFetching || searchTerm.trim() !== debouncedSearch);

  return (
    <AsyncFacetedFilter
      title="Item"
      options={options}
      selected={selected}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      isSearching={isSearching}
      emptyMessage={
        hasSearch
          ? 'No results found.'
          : searchTerm.trim()
            ? 'Keep typing to search your items'
            : 'Start typing to search your items'
      }
      onChange={(next) => {
        setFilters({ item: next.length ? next.map((o) => o.value) : null });
        setTableState({ page: null });
        track('Applied PG Insights Filter', {
          filter_type: 'item',
          value_names: next.map((option) => option.label),
          value_count: next.length,
        });
      }}
    />
  );
}
